import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import api, { errMsg } from '../api/client';
import CategoryIcon from './CategoryIcon';
import { categoryName } from '../i18n';

const today = () => new Date().toISOString().slice(0, 10);

/**
 * The 10-second expense entry: big amount field, tap a category tile,
 * optional details. `access` is 'owner' | 'client'.
 */
const AddExpenseDialog = ({ open, onClose, onSaved, projectId, categories, access }) => {
  const { t } = useTranslation();
  const [amount, setAmount] = useState('');
  const [categoryId, setCategoryId] = useState(null);
  const [paidTo, setPaidTo] = useState('');
  const [note, setNote] = useState('');
  const [date, setDate] = useState(today());
  const [visibleToClient, setVisibleToClient] = useState(true);
  const [paidBy, setPaidBy] = useState('constructor');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setAmount('');
    setCategoryId(null);
    setPaidTo('');
    setNote('');
    setDate(today());
    setVisibleToClient(true);
    setPaidBy('constructor');
    setError('');
  };

  const submit = async () => {
    setError('');
    setBusy(true);
    try {
      await api.post(`/api/projects/${projectId}/expenses`, {
        amount: Number(amount),
        categoryId,
        paidTo: paidTo || undefined,
        note: note || undefined,
        expenseDate: date,
        visibleToClient,
        paidBy: access === 'owner' ? paidBy : undefined,
      });
      reset();
      onSaved();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('expense.add')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            label={t('expense.amount')}
            value={amount}
            onChange={(e) => setAmount(e.target.value.replace(/[^\d]/g, ''))}
            type="tel"
            autoFocus
            InputProps={{
              startAdornment: <InputAdornment position="start">₹</InputAdornment>,
              style: { fontSize: 24, fontWeight: 700 },
            }}
          />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t('expense.category')}
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))',
                gap: 1,
              }}
            >
              {categories.map((cat) => (
                <Box
                  key={cat.id}
                  onClick={() => setCategoryId(cat.id)}
                  sx={(theme) => ({
                    p: 1,
                    borderRadius: 2,
                    border: `2px solid ${categoryId === cat.id ? theme.palette.primary.main : theme.palette.divider}`,
                    bgcolor: categoryId === cat.id ? 'action.selected' : 'transparent',
                    cursor: 'pointer',
                    textAlign: 'center',
                    transition: 'all 0.12s',
                  })}
                >
                  <CategoryIcon
                    icon={cat.icon}
                    sx={{ fontSize: 22, color: categoryId === cat.id ? 'primary.main' : 'text.secondary' }}
                  />
                  <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.2 }}>
                    {categoryName(cat)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {access === 'owner' && (
            <ToggleButtonGroup
              exclusive
              fullWidth
              size="small"
              value={paidBy}
              onChange={(_, v) => v && setPaidBy(v)}
            >
              <ToggleButton value="constructor">{t('expense.paidByMe')}</ToggleButton>
              <ToggleButton value="client">{t('expense.clientPaid')}</ToggleButton>
            </ToggleButtonGroup>
          )}

          <TextField label={t('expense.paidTo')} value={paidTo} onChange={(e) => setPaidTo(e.target.value)} />
          <Stack direction="row" spacing={2}>
            <TextField
              label={t('expense.date')}
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField label={t('expense.note')} value={note} onChange={(e) => setNote(e.target.value)} />
          </Stack>

          {access === 'owner' && (
            <FormControlLabel
              control={
                <Switch checked={visibleToClient} onChange={(e) => setVisibleToClient(e.target.checked)} />
              }
              label={t('expense.visibleToClient')}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="contained" disabled={!amount || !categoryId || busy} onClick={submit}>
          {t('expense.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddExpenseDialog;
